// PropertyBlock — per-property editable block used by HomeBookingWidget.
//
// Renders:
//   • Property header (thumb + name + duplicate/remove actions)
//   • Property-level check-in/check-out dates
//   • One RoomSubCard per room (independently editable)
//   • Bulk-add rooms/tents helper
//   • Property-level estimate (indicative rate) + notes textarea
//   • Optional "Apply dates to all" quick-copy link
//
// All state lives in the parent — this component receives `block` +
// callbacks (`onChange`, `onDuplicate`, `onRemove`, `applyDatesToAll`)
// so it stays a pure UI layer over the shared BookingContext.

import { useMemo, useState } from "react";
import {
  Calendar as CalendarIcon, Copy, Minus, Plus, Trash2,
} from "lucide-react";
import { isBivouac, getAccommodationTypes } from "@/lib/propertyType";
import { getCampLabels } from "@/lib/bookingCamps";
import { BOARD_ORDER, BOARD_FIELD_LABEL, pickBoardLabel } from "@/lib/boardBasis";
import { useFormatPrice } from "@/contexts/CurrencyContext";
import PropertyThumb from "@/components/PropertyThumb";

const pick = (obj, lang) => (obj?.[lang] ?? obj?.en ?? "");

const newRoom = (idx = 0) => ({
  id: `r_${Date.now().toString(36)}_${idx}_${Math.random().toString(36).slice(2, 6)}`,
  room_type: "",
  adults: 2,
  children: 0,
  infants: 0,
  board_basis: "breakfast",
});

// COPY dictionary — subset of the widget-level COPY needed inside a
// PropertyBlock instance. Kept co-located so the file is self-contained.
const COPY = {
  check_in:     { en: "Check-in",   fr: "Arrivée", es: "Llegada" },
  check_out:    { en: "Check-out",  fr: "Départ",  es: "Salida"  },
  adults:       { en: "Adults",     fr: "Adultes", es: "Adultos" },
  children:     { en: "Children",   fr: "Enfants", es: "Niños"   },
  infants:      { en: "Infants",    fr: "Bébés",   es: "Bebés"   },
  room_type:    { en: "Room type",  fr: "Type de chambre", es: "Tipo de habitación" },
  tent_type:    { en: "Tent type",  fr: "Type de tente",   es: "Tipo de tienda" },
  any:          { en: "Any (we'll suggest)", fr: "Indifférent (nous proposerons)", es: "Indistinto (sugeriremos)" },
  notes_label:  { en: "Notes for this property", fr: "Notes pour cette adresse", es: "Notas para esta propiedad" },
  notes_ph:     { en: "Connecting rooms, anniversary, early arrival, dietary needs, accessibility…", fr: "Chambres communicantes, anniversaire, arrivée tôt, régime alimentaire, accessibilité…", es: "Habitaciones comunicadas, aniversario, llegada temprana, dieta, accesibilidad…" },
  duplicate:    { en: "Duplicate stay",  fr: "Dupliquer le séjour", es: "Duplicar estancia" },
  remove:       { en: "Remove",          fr: "Retirer",             es: "Quitar" },
  apply_all:    { en: "Apply dates to all", fr: "Appliquer les dates à tous", es: "Aplicar fechas a todos" },
  room_n:       { en: "Room",   fr: "Chambre", es: "Habitación" },
  tent_n:       { en: "Tent",   fr: "Tente",   es: "Tienda" },
  add_room:     { en: "Add another room", fr: "Ajouter une chambre", es: "Añadir habitación" },
  add_tent:     { en: "Add another tent", fr: "Ajouter une tente",   es: "Añadir tienda" },
  remove_room:  { en: "Remove room", fr: "Retirer la chambre", es: "Quitar habitación" },
  remove_tent:  { en: "Remove tent", fr: "Retirer la tente",   es: "Quitar tienda" },
  copy_below:   { en: "Copy to rooms below", fr: "Copier vers les chambres ci-dessous", es: "Copiar a las habitaciones siguientes" },
  copy_below_t: { en: "Copy to tents below", fr: "Copier vers les tentes ci-dessous",   es: "Copiar a las tiendas siguientes" },
  bulk_or:      { en: "or", fr: "ou", es: "o" },
  bulk_label:   { en: "Bulk add rooms", fr: "Ajout rapide", es: "Añadir varias" },
  bulk_label_t: { en: "Bulk add tents", fr: "Ajout rapide", es: "Añadir varias" },
  bulk_generate:{ en: "Generate rooms", fr: "Générer les chambres", es: "Generar habitaciones" },
  bulk_generate_t:{ en: "Generate tents", fr: "Générer les tentes",   es: "Generar tiendas" },
  est_night:    { en: "night",  fr: "nuit",  es: "noche" },
  est_nights:   { en: "nights", fr: "nuits", es: "noches" },
  est_room:     { en: "room",   fr: "chambre", es: "habitación" },
  est_rooms:    { en: "rooms",  fr: "chambres", es: "habitaciones" },
  est_tent:     { en: "tent",   fr: "tente",  es: "tienda" },
  est_tents:    { en: "tents",  fr: "tentes", es: "tiendas" },
  est_loading:  { en: "Loading rates…", fr: "Chargement des tarifs…", es: "Cargando tarifas…" },
  est_no_data:  { en: "Rates available on request", fr: "Tarifs sur demande", es: "Tarifas a petición" },
  pick_dates:   { en: "Pick dates to see a ballpark", fr: "Choisir les dates pour estimer", es: "Elige fechas para estimar" },
  bivouac_kicker: { en: "Bivouac · Desert", fr: "Bivouac · Désert", es: "Vivac · Desierto" },
  hotel_kicker:   { en: "Hotel · Kasbah",  fr: "Hôtel · Kasbah",   es: "Hotel · Kasbah" },
};

// ── Stepper — used for adults/children/infants controls ───────────
const Stepper = ({ value, onChange, min = 0, label, testIdPrefix }) => (
  <div className="flex flex-col gap-1">
    <span className="text-[10px] tracking-[0.25em] uppercase text-[#5C5248]">{label}</span>
    <div className="flex items-center gap-1.5 bg-white border border-[#2C2621]/15 px-1.5 py-1.5">
      <button
        type="button"
        onClick={() => onChange(Math.max(min, value - 1))}
        className="w-7 h-7 inline-flex items-center justify-center text-[#5C5248] hover:text-[#C16542] disabled:opacity-40"
        disabled={value <= min}
        aria-label={`Decrease ${label}`}
        data-testid={`${testIdPrefix}-minus`}
      >
        <Minus className="w-3.5 h-3.5" strokeWidth={1.7} />
      </button>
      <span
        className="flex-1 text-center text-sm tabular-nums text-[#2C2621]"
        data-testid={`${testIdPrefix}-value`}
      >
        {value}
      </span>
      <button
        type="button"
        onClick={() => onChange(value + 1)}
        className="w-7 h-7 inline-flex items-center justify-center text-[#5C5248] hover:text-[#C16542]"
        aria-label={`Increase ${label}`}
        data-testid={`${testIdPrefix}-plus`}
      >
        <Plus className="w-3.5 h-3.5" strokeWidth={1.7} />
      </button>
    </div>
  </div>
);

// ── Single room sub-card — independently editable ──────────────────
const RoomSubCard = ({
  room, idx, total, bivouac, accomTypes, lang,
  onChange, onRemove, onCopyBelow,
  subtotalEur,
}) => {
  const set = (key, value) => onChange({ ...room, [key]: value });
  const inputCls = "w-full bg-white border border-[#2C2621]/15 hover:border-[#C16542] focus:border-[#C16542] outline-none px-3 py-2.5 text-sm text-[#2C2621]";
  const labelCls = "text-[10px] tracking-[0.25em] uppercase text-[#5C5248]";
  const headerLabel = bivouac ? pick(COPY.tent_n, lang) : pick(COPY.room_n, lang);
  const typeLabel  = bivouac ? pick(COPY.tent_type, lang) : pick(COPY.room_type, lang);
  const copyLabel  = bivouac ? pick(COPY.copy_below_t, lang) : pick(COPY.copy_below, lang);
  const hasRoomsBelow = idx < total - 1;
  return (
    <div
      className="bg-white border border-[#2C2621]/12 p-3.5 md:p-4"
      data-testid={`home-booking-room-${room.id}`}
    >
      <div className="flex items-center justify-between gap-3 mb-3">
        <p className="text-[10px] tracking-[0.3em] uppercase text-[#A07042]">
          {headerLabel} {String(idx + 1).padStart(2, "0")}
        </p>
        <div className="flex items-center gap-2">
          {subtotalEur ? (
            <span
              className="text-[11px] text-[#C16542] font-medium"
              data-testid={`home-booking-room-${room.id}-subtotal`}
            >
              ≈ {subtotalEur}
            </span>
          ) : null}
          {hasRoomsBelow ? (
            <button
              type="button"
              onClick={onCopyBelow}
              className="inline-flex items-center gap-1 px-2 py-1 border border-[#2C2621]/15 hover:border-[#C16542] text-[#5C5248] hover:text-[#C16542] text-[10px] tracking-[0.18em] uppercase transition-colors"
              aria-label={copyLabel}
              title={copyLabel}
              data-testid={`home-booking-room-${room.id}-copy-below`}
            >
              <Copy className="w-3 h-3" strokeWidth={1.7} />
              <span className="hidden sm:inline">{copyLabel}</span>
            </button>
          ) : null}
          {total > 1 ? (
            <button
              type="button"
              onClick={onRemove}
              className="inline-flex items-center gap-1 px-2 py-1 border border-[#2C2621]/15 hover:border-[#A35133] text-[#5C5248] hover:text-[#A35133] text-[10px] tracking-[0.18em] uppercase transition-colors"
              aria-label={bivouac ? pick(COPY.remove_tent, lang) : pick(COPY.remove_room, lang)}
              title={bivouac ? pick(COPY.remove_tent, lang) : pick(COPY.remove_room, lang)}
              data-testid={`home-booking-room-${room.id}-remove`}
            >
              <Trash2 className="w-3 h-3" strokeWidth={1.7} />
            </button>
          ) : null}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-12 gap-3">
        <div className="col-span-2 md:col-span-4 flex flex-col gap-1">
          <span className={labelCls}>{typeLabel}</span>
          <select
            value={room.room_type}
            onChange={(e) => set("room_type", e.target.value)}
            className={`${inputCls} appearance-none bg-no-repeat`}
            style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path fill='%235C5248' d='M5 6 0 0h10z'/></svg>\")", backgroundPosition: "right 0.75rem center", paddingRight: "1.75rem" }}
            data-testid={`home-booking-room-${room.id}-roomtype`}
          >
            <option value="">{pick(COPY.any, lang)}</option>
            {accomTypes.map((opt) => (
              <option key={opt.key} value={pick(opt.name, lang)}>
                {pick(opt.name, lang)}
                {opt.capacity ? ` · ${pick(opt.capacity, lang)}` : ""}
              </option>
            ))}
          </select>
        </div>

        <div className="col-span-2 md:col-span-1">
          <Stepper value={room.adults}   onChange={(v) => set("adults", v)}   min={1} label={pick(COPY.adults, lang)}    testIdPrefix={`home-booking-room-${room.id}-adults`} />
        </div>
        <div className="col-span-1 md:col-span-1">
          <Stepper value={room.children} onChange={(v) => set("children", v)} min={0} label={pick(COPY.children, lang)} testIdPrefix={`home-booking-room-${room.id}-children`} />
        </div>
        <div className="col-span-1 md:col-span-1">
          <Stepper value={room.infants}  onChange={(v) => set("infants", v)}  min={0} label={pick(COPY.infants, lang)}  testIdPrefix={`home-booking-room-${room.id}-infants`} />
        </div>

        <div className="col-span-2 md:col-span-5 flex flex-col gap-1">
          <span className={labelCls}>{pick(BOARD_FIELD_LABEL, lang)}</span>
          <div className="flex flex-wrap gap-1.5">
            {BOARD_ORDER.map((key) => {
              const active = room.board_basis === key;
              return (
                <button
                  type="button"
                  key={key}
                  onClick={() => set("board_basis", key)}
                  aria-pressed={active}
                  className={`text-[11px] tracking-[0.18em] uppercase px-2.5 py-2 border transition-colors ${
                    active
                      ? "bg-[#C16542] border-[#C16542] text-[#FDFBF7]"
                      : "bg-white border-[#2C2621]/15 text-[#5C5248] hover:border-[#C16542] hover:text-[#C16542]"
                  }`}
                  data-testid={`home-booking-room-${room.id}-board-${key}`}
                >
                  {pickBoardLabel(key, lang, { bivouac })}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Per-property editable block ────────────────────────────────────
export default function PropertyBlock({
  block, hotel, lang, onChange, onDuplicate, onRemove, today, applyDatesToAll, estimate,
}) {
  const { fromEUR } = useFormatPrice();
  const bivouac = isBivouac(block.slug);
  const accomTypes = useMemo(() => getAccommodationTypes(block.slug), [block.slug]);
  const [bulkCount, setBulkCount] = useState("");

  const setField = (key, value) => onChange({ ...block, [key]: value });
  const setRoom = (idx, next) => {
    const rooms = block.rooms.map((r, i) => (i === idx ? next : r));
    onChange({ ...block, rooms });
  };
  const addRoom = () => {
    onChange({ ...block, rooms: [...block.rooms, newRoom(block.rooms.length)] });
  };
  const removeRoom = (idx) => {
    if (block.rooms.length <= 1) return;
    onChange({ ...block, rooms: block.rooms.filter((_, i) => i !== idx) });
  };
  const generateRooms = () => {
    const parsed = parseInt(bulkCount, 10);
    if (!Number.isFinite(parsed)) return;
    const n = Math.max(1, Math.min(50, parsed));
    const current = block.rooms.length;
    if (n === current) return;
    let nextRooms;
    if (n > current) {
      const additions = [];
      for (let i = current; i < n; i++) additions.push(newRoom(i));
      nextRooms = [...block.rooms, ...additions];
    } else {
      nextRooms = block.rooms.slice(0, n);
    }
    onChange({ ...block, rooms: nextRooms });
    setBulkCount("");
  };
  const copyRoomBelow = (idx) => {
    const src = block.rooms[idx];
    if (!src) return;
    const fields = ["room_type", "adults", "children", "infants", "board_basis"];
    const nextRooms = block.rooms.map((r, i) => {
      if (i <= idx) return r;
      const copied = { ...r };
      fields.forEach((k) => { copied[k] = src[k]; });
      return copied;
    });
    onChange({ ...block, rooms: nextRooms });
  };

  const minCheckOut = useMemo(() => {
    if (!block.check_in) return today;
    const d = new Date(block.check_in);
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  }, [block.check_in, today]);

  const inputCls = "w-full bg-white border border-[#2C2621]/15 hover:border-[#C16542] focus:border-[#C16542] outline-none px-3 py-2.5 text-sm text-[#2C2621]";
  const labelCls = "text-[10px] tracking-[0.25em] uppercase text-[#5C5248]";

  return (
    <div
      className="bg-[#F2EBE1]/40 border border-[#2C2621]/10 p-4 md:p-5"
      data-testid={`home-booking-block-${block.rowId}`}
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-start gap-3 min-w-0">
          <PropertyThumb
            slug={block.slug}
            alt={hotel?.name || block.slug}
            aspectRatio="4/3"
            className="w-16 h-12 md:w-20 md:h-16 shrink-0"
            testId={`home-booking-block-${block.rowId}-thumb`}
            cmsSlot={`booking-widget.thumb.${block.slug}`}
          />
          <div className="min-w-0">
            <p className="overline !text-[#C16542] !text-[9px]">
              {bivouac ? pick(COPY.bivouac_kicker, lang) : pick(COPY.hotel_kicker, lang)}
            </p>
            <h3 className="font-serif-x text-base md:text-lg text-[#2C2621] mt-0.5 leading-tight break-words">
              {hotel ? hotel.name : block.slug}
            </h3>
            {Array.isArray(block.campSlugs) && block.campSlugs.length > 0 ? (
              <p
                className="text-[10px] tracking-[0.2em] uppercase text-[#C16542] mt-1 leading-relaxed"
                data-testid={`home-booking-block-${block.rowId}-camp-label`}
              >
                {getCampLabels(block.slug, block.campSlugs, lang).join("  ·  ")}
              </p>
            ) : null}
            {hotel?.location ? (
              <p className="text-[11px] text-[#5C5248] mt-0.5 break-words">{pick(hotel.location, lang)}</p>
            ) : null}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            type="button"
            onClick={onDuplicate}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-[#2C2621]/15 hover:border-[#C16542] text-[#5C5248] hover:text-[#C16542] text-[10px] tracking-[0.2em] uppercase transition-colors"
            aria-label={pick(COPY.duplicate, lang)}
            title={pick(COPY.duplicate, lang)}
            data-testid={`home-booking-block-${block.rowId}-duplicate`}
          >
            <Copy className="w-3 h-3" strokeWidth={1.7} />
            <span className="hidden sm:inline">{pick(COPY.duplicate, lang)}</span>
          </button>
          <button
            type="button"
            onClick={onRemove}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-[#2C2621]/15 hover:border-[#A35133] text-[#5C5248] hover:text-[#A35133] text-[10px] tracking-[0.2em] uppercase transition-colors"
            aria-label={pick(COPY.remove, lang)}
            title={pick(COPY.remove, lang)}
            data-testid={`home-booking-block-${block.rowId}-remove`}
          >
            <Trash2 className="w-3 h-3" strokeWidth={1.7} />
            <span className="hidden sm:inline">{pick(COPY.remove, lang)}</span>
          </button>
        </div>
      </div>

      {/* Property-level dates */}
      <div className="grid grid-cols-2 md:grid-cols-12 gap-3 mb-3">
        <div className="col-span-1 md:col-span-3 flex flex-col gap-1">
          <span className={labelCls}>{pick(COPY.check_in, lang)}</span>
          <div className="relative">
            <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#5C5248]" strokeWidth={1.6} />
            <input
              type="date"
              min={today}
              value={block.check_in}
              onChange={(e) => setField("check_in", e.target.value)}
              className={`${inputCls} pl-8`}
              data-testid={`home-booking-block-${block.rowId}-checkin`}
            />
          </div>
        </div>
        <div className="col-span-1 md:col-span-3 flex flex-col gap-1">
          <span className={labelCls}>{pick(COPY.check_out, lang)}</span>
          <div className="relative">
            <CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#5C5248]" strokeWidth={1.6} />
            <input
              type="date"
              min={minCheckOut}
              value={block.check_out}
              onChange={(e) => setField("check_out", e.target.value)}
              className={`${inputCls} pl-8`}
              data-testid={`home-booking-block-${block.rowId}-checkout`}
            />
          </div>
        </div>
      </div>

      {/* Rooms / Tents — independently configurable */}
      <div className="space-y-3">
        {block.rooms.map((room, idx) => {
          const roomEst = estimate?.perRoom?.[idx];
          return (
            <RoomSubCard
              key={room.id}
              room={room}
              idx={idx}
              total={block.rooms.length}
              bivouac={bivouac}
              accomTypes={accomTypes}
              lang={lang}
              onChange={(next) => setRoom(idx, next)}
              onRemove={() => removeRoom(idx)}
              onCopyBelow={() => copyRoomBelow(idx)}
              subtotalEur={roomEst?.subtotal ? fromEUR(roomEst.subtotal) : null}
            />
          );
        })}
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={addRoom}
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-[#2C2621]/15 hover:border-[#C16542] text-[#5C5248] hover:text-[#C16542] text-[10px] tracking-[0.22em] uppercase transition-colors"
            data-testid={`home-booking-block-${block.rowId}-add-room`}
          >
            <Plus className="w-3 h-3" strokeWidth={1.7} />
            {bivouac ? pick(COPY.add_tent, lang) : pick(COPY.add_room, lang)}
          </button>
          <span className="text-[10px] tracking-[0.22em] uppercase text-[#5C5248]/55">
            {pick(COPY.bulk_or, lang)}
          </span>
          <div
            className="inline-flex items-center gap-1.5 px-2 py-1.5 border border-[#2C2621]/15 bg-white"
            data-testid={`home-booking-block-${block.rowId}-bulk-group`}
          >
            <label
              htmlFor={`home-booking-block-${block.rowId}-bulk-input`}
              className="text-[10px] tracking-[0.22em] uppercase text-[#5C5248] sr-only sm:not-sr-only"
            >
              {bivouac ? pick(COPY.bulk_label_t, lang) : pick(COPY.bulk_label, lang)}
            </label>
            <input
              id={`home-booking-block-${block.rowId}-bulk-input`}
              type="number"
              min="1"
              max="50"
              value={bulkCount}
              onChange={(e) => setBulkCount(e.target.value.replace(/[^0-9]/g, ""))}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  generateRooms();
                }
              }}
              placeholder="5"
              className="w-14 bg-white border border-[#2C2621]/15 hover:border-[#C16542] focus:border-[#C16542] outline-none px-2 py-1 text-sm text-[#2C2621] tabular-nums text-center"
              aria-label={bivouac ? pick(COPY.bulk_label_t, lang) : pick(COPY.bulk_label, lang)}
              data-testid={`home-booking-block-${block.rowId}-bulk-count`}
            />
            <button
              type="button"
              onClick={generateRooms}
              disabled={!bulkCount}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#C16542] hover:bg-[#A35133] text-[#FDFBF7] text-[10px] tracking-[0.22em] uppercase transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid={`home-booking-block-${block.rowId}-bulk-generate`}
            >
              {bivouac ? pick(COPY.bulk_generate_t, lang) : pick(COPY.bulk_generate, lang)}
            </button>
          </div>
        </div>
        {/* Property-level subtotal */}
        {estimate && estimate.subtotal && !estimate.loading ? (
          <span
            className="text-sm text-[#2C2621]"
            data-testid={`home-booking-block-${block.rowId}-estimate`}
          >
            <span className="text-[#C16542] font-medium">≈ {fromEUR(estimate.subtotal)}</span>
            <span className="text-[11px] text-[#5C5248] ml-2">
              {estimate.nights} {estimate.nights === 1 ? pick(COPY.est_night, lang) : pick(COPY.est_nights, lang)}
              {" · "}
              {block.rooms.length} {(() => {
                if (block.rooms.length === 1) return bivouac ? pick(COPY.est_tent, lang) : pick(COPY.est_room, lang);
                return bivouac ? pick(COPY.est_tents, lang) : pick(COPY.est_rooms, lang);
              })()}
            </span>
          </span>
        ) : estimate?.loading ? (
          <span className="text-[11px] italic text-[#5C5248]/70">{pick(COPY.est_loading, lang)}</span>
        ) : (
          <span className="text-[11px] italic text-[#5C5248]/70" data-testid={`home-booking-block-${block.rowId}-estimate-empty`}>
            {!block.check_in || !block.check_out
              ? pick(COPY.pick_dates, lang)
              : pick(COPY.est_no_data, lang)}
          </span>
        )}
      </div>

      {/* Property-level notes */}
      <div className="mt-4 flex flex-col gap-1">
        <span className={labelCls}>{pick(COPY.notes_label, lang)}</span>
        <textarea
          rows={2}
          value={block.notes}
          onChange={(e) => setField("notes", e.target.value)}
          placeholder={pick(COPY.notes_ph, lang)}
          className={`${inputCls} resize-none leading-relaxed`}
          data-testid={`home-booking-block-${block.rowId}-notes`}
        />
      </div>

      {applyDatesToAll && block.check_in && block.check_out ? (
        <div className="mt-3 text-right">
          <button
            type="button"
            onClick={applyDatesToAll}
            className="text-[10px] tracking-[0.25em] uppercase text-[#C16542] hover:text-[#A35133]"
            data-testid={`home-booking-block-${block.rowId}-apply-all`}
          >
            ↺ {pick(COPY.apply_all, lang)}
          </button>
        </div>
      ) : null}
    </div>
  );
}
